// src/layouts/AdminLayout.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Layout, Menu, Avatar, Popover, Typography, Badge, List, Button, Spin, Empty, Divider, message, // Added message for potential future use
    Tooltip
} from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    SolutionOutlined,
    BellOutlined,
    MessageOutlined, // Assuming you might have a messages page
    SettingOutlined,
    ProjectOutlined, // Assuming you might have a projects page
    LogoutOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    ProfileOutlined, // Icon for the profile menu item
    CheckOutlined,   // Icon for marking notification read
    ClockCircleOutlined // Icon for notification timestamp
} from '@ant-design/icons';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext'; // Adjust path if needed
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../services/notificationService';

// Destructure Layout components and Typography
const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;

// --- Styled Components ---
const LogoContainer = styled.div`
  height: 32px;
  margin: 16px;
  background: rgba(255, 255, 255, 0.2); // Placeholder logo background
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  border-radius: 6px;
  font-size: 1.1rem;
  overflow: hidden; // Hide text when collapsed
  transition: all 0.3s; // Smooth transition for logo text appearance
`;

const StyledLayout = styled(Layout)`
  min-height: 100vh;
`;

const StyledHeader = styled(Header)`
  padding: 0 24px; // Standard Ant Design padding
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff; // White header background
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08); // Subtle shadow below header
  position: sticky; // Optional: make header sticky
  top: 0;
  z-index: 10; // Ensure header stays above content during scroll
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 24px; // Slightly more space between header items
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.3; // Improved line height
`;

const NotificationItem = styled(List.Item)`
    cursor: pointer;
    transition: background-color 0.3s;
    padding: 10px 14px !important; // Slightly more padding
    border-radius: 4px;
    border-bottom: 1px solid #f0f0f0; // Add subtle separator

    &:last-child {
        border-bottom: none; // Remove border for last item
    }

    &:hover {
        background-color: #e6f7ff; // Light blue hover
    }

    &.unread {
        background-color: #f0f8ff; // Lighter blue for unread
        font-weight: 500; // Slightly bolder text for unread title
    }
`;
// --- End Styled Components ---


// --- Helper function for notification icons ---
const getNotificationIcon = (type) => {
    // Map types to icons and colors
    const iconMap = {
        quote_received: { icon: <SolutionOutlined />, color: '#1890ff' },
        user_added: { icon: <UserOutlined />, color: '#52c41a' },
        quote_status_change: { icon: <BellOutlined />, color: '#faad14' },
        default: { icon: <BellOutlined />, color: '#bfbfbf' }
    };
    const { icon, color } = iconMap[type] || iconMap.default;
    return <Avatar style={{ backgroundColor: color }} icon={icon} />;
};

// --- Main AdminLayout Component ---
const AdminLayout = () => {
    const [collapsed, setCollapsed] = useState(false); // Sidebar collapsed state
    const { user, logoutUser, isAdmin, isManager } = useAuth(); // User data and roles from context
    const location = useLocation(); // Current location for menu highlighting
    const navigate = useNavigate(); // Hook for programmatic navigation

    // --- Popover Visibility States ---
    const [userPopoverVisible, setUserPopoverVisible] = useState(false); // State for user menu popover
    const [notificationPopoverVisible, setNotificationPopoverVisible] = useState(false); // State for notification popover

    // --- Notification State & Functions ---
    const [notifications, setNotifications] = useState([]); // Array of notifications
    const [loadingNotifications, setLoadingNotifications] = useState(true); // Loading state for notifications

    // Fetch notifications using useCallback for stability
    const fetchNotificationsData = useCallback(async () => {
        setLoadingNotifications(true);
        try {
            const fetchedNotifications = await getNotifications(); // Call mock service
            setNotifications(fetchedNotifications);
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            // Consider showing an Ant Design message here
            // message.error('Could not load notifications.');
        } finally {
            setLoadingNotifications(false);
        }
    }, []); // No dependencies, usually fetched once or via polling/websockets

    // Fetch notifications on component mount
    useEffect(() => {
        fetchNotificationsData();
        // Optional: Implement polling or WebSocket listener for real-time updates
        // const intervalId = setInterval(fetchNotificationsData, 60000); // Example: Fetch every 60 seconds
        // return () => clearInterval(intervalId); // Cleanup interval on unmount
    }, [fetchNotificationsData]);

    // Calculate unread notification count for the badge
    const unreadCount = notifications.filter(n => !n.read).length;

    // Mark a single notification as read
    const handleMarkRead = useCallback(async (id, e) => {
        if (e && e.stopPropagation) {
            e.stopPropagation(); // Prevent parent popover/item click events
        }
        const notification = notifications.find(n => n.id === id);
        // Only proceed if the notification exists and is currently unread
        if (notification && !notification.read) {
             try {
                await markNotificationRead(id); // Call mock service
                // Optimistic UI update: Mark as read locally immediately
                setNotifications(prev =>
                    prev.map(n => (n.id === id ? { ...n, read: true } : n))
                );
            } catch (error) {
                console.error("Failed to mark notification as read:", error);
                message.error('Failed to mark notification as read.'); // Provide feedback
            }
        } else if (!notification) {
             console.warn("Notification not found for marking read:", id);
        }
    }, [notifications]); // Depends on the notifications state

    // Mark all notifications as read
    const handleMarkAllRead = async () => {
        if (unreadCount === 0) return; // Don't do anything if no unread messages
        setLoadingNotifications(true); // Show loading indicator (optional)
        try {
            const result = await markAllNotificationsRead(); // Call mock service
            if (result.updated) {
                // Optimistic update: Mark all as read locally
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            }
             // message.success('All notifications marked as read.'); // Optional success message
        } catch (error) {
            console.error("Failed to mark all notifications as read:", error);
            message.error('Failed to mark all notifications as read.');
        } finally {
            setLoadingNotifications(false);
        }
    };

    // Handle click on a notification item in the popover
    const handleNotificationClick = (item) => {
        console.log("Clicked notification in popover, navigating to details:", item.id);
        // Mark as read if it wasn't already
        if (!item.read) {
             handleMarkRead(item.id); // No event object needed here
        }
        // Navigate to the main notifications page with a query param to view this specific notification
        navigate(`/notifications?view=${item.id}`);
        setNotificationPopoverVisible(false); // Close the notification popover after clicking
    };

    // --- Popover Contents ---

    // Notification Popover Content
    const notificationPopoverContent = (
        <div style={{ width: '350px' }}> {/* Fixed width for consistency */}
            {loadingNotifications && notifications.length === 0 ? ( // Show spinner only on initial load
                <div style={{ textAlign: 'center', padding: '40px 20px' }}><Spin tip="Loading..." /></div>
            ) : notifications.length > 0 ? (
                <>
                    <List
                        itemLayout="horizontal"
                        dataSource={notifications.slice(0, 5)} // Limit to recent 5 in popover
                        renderItem={item => (
                            <NotificationItem
                                onClick={() => handleNotificationClick(item)} // Handle click on the item itself
                                className={!item.read ? 'unread' : ''} // Style unread items
                                actions={!item.read ? [ // Action only shown if unread
                                    <Tooltip title="Mark as read">
                                        <Button
                                            type="text" size="small" shape="circle" // Use circle for compact action
                                            icon={<CheckOutlined />}
                                            onClick={(e) => handleMarkRead(item.id, e)} // Pass event to stop propagation
                                            style={{ color: '#1890ff' }}
                                        />
                                    </Tooltip>
                                ] : undefined}
                            >
                                <List.Item.Meta
                                    avatar={getNotificationIcon(item.type)}
                                    title={<Text strong={!item.read}>{item.message}</Text>}
                                    description={
                                        <Text type="secondary" style={{ fontSize: '0.85em' }}>
                                            <ClockCircleOutlined style={{ marginRight: 5 }} />
                                            {new Date(item.timestamp).toLocaleString()}
                                        </Text>
                                    }
                                />
                            </NotificationItem>
                        )}
                    />
                    <Divider style={{ margin: '4px 0' }} /> {/* Thinner divider */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px' }}>
                        <Button
                             type="link" size="small"
                             onClick={handleMarkAllRead}
                             disabled={unreadCount === 0 || loadingNotifications}
                             loading={loadingNotifications && unreadCount > 0} // Show loading only when marking all
                        >
                            Mark all as read
                        </Button>
                        <Button type="link" size="small" onClick={() => { setNotificationPopoverVisible(false); navigate('/notifications'); }}>
                            View All
                        </Button>
                    </div>
                </>
            ) : ( // No notifications state
                <Empty description="No notifications" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '30px 0'}}/>
            )}
        </div>
    );

    // --- User Menu Popover Content and Handler ---
    // Function to handle clicks within the user menu popover
    const handleUserMenuClick = (menuInfo) => {
        const { key } = menuInfo;
        setUserPopoverVisible(false); // Close the popover regardless of item clicked
        if (key === 'profile') {
            navigate('/profile'); // Navigate to the profile page
        } else if (key === 'logout') {
            logoutUser(); // Call the logout function from AuthContext
        }
        // Add other cases like 'settings' if needed
    };

    // Define the content of the user popover menu
    const userMenuContent = (
       <Menu
            mode="vertical"
            selectable={false} // Items aren't selectable in the traditional sense
            style={{ borderRight: 0, width: 180 }} // Remove border, set width
            onClick={handleUserMenuClick} // Use the handler defined above
        >
            <Menu.Item key="profile" icon={<ProfileOutlined />}>
                My Profile
            </Menu.Item>
            <Menu.Divider /> {/* Visual separator */}
            <Menu.Item key="logout" icon={<LogoutOutlined />} danger> {/* Use 'danger' for logout style */}
                Logout
            </Menu.Item>
       </Menu>
    );

    // --- Sidebar Menu ---
    // Function to determine which menu item should be highlighted based on the current path
    const getSelectedKeys = () => {
        const path = location.pathname;
        // Check more specific paths first
        if (path.startsWith('/profile')) return ['/profile'];
        if (path.startsWith('/settings')) return ['/settings'];
        if (path.startsWith('/projects')) return ['/projects'];
        if (path.startsWith('/notifications')) return ['/notifications'];
        if (path.startsWith('/messages')) return ['/messages'];
        if (path.startsWith('/users')) return ['/users'];
        if (path.startsWith('/quotes')) return ['/quotes'];
        if (path === '/') return ['/']; // Dashboard is the index route
        // Fallback if none of the above match (less likely with nested routes)
        return [path];
    };

    // Define the items for the main sidebar navigation menu
    const menuItems = [
        { key: '/', icon: <DashboardOutlined />, label: <Link to="/">Dashboard</Link> },
        { key: '/quotes', icon: <SolutionOutlined />, label: <Link to="/quotes">Quotes</Link> },
        // Conditionally render menu items based on user role
        isAdmin && { key: '/users', icon: <UserOutlined />, label: <Link to="/users">Users</Link> },
        (isAdmin || isManager) && { key: '/messages', icon: <MessageOutlined />, label: <Link to="/messages">Messages</Link> },
        { key: '/notifications', icon: <BellOutlined />, label: <Link to="/notifications">Notifications</Link> },
        { key: '/projects', icon: <ProjectOutlined />, label: <Link to="/projects">Projects</Link> },
        { key: '/settings', icon: <SettingOutlined />, label: <Link to="/settings">Settings</Link> },
        // Optional: If you want 'My Profile' also in the main sidebar
        // { key: '/profile', icon: <ProfileOutlined />, label: <Link to="/profile">My Profile</Link> },
    ].filter(Boolean); // Filter out any 'false' entries from conditional rendering


    // --- Component Return JSX ---
    return (
        <StyledLayout>
            {/* Sidebar Navigation */}
            <Sider
                trigger={null} // No default trigger icon
                collapsible
                collapsed={collapsed}
                theme="dark" // Dark theme for sidebar
                style={{
                    overflow: 'auto', // Allow scrolling if menu is long
                    height: '100vh',
                    position: 'fixed', // Fixed position sidebar
                    left: 0,
                    top: 0,
                    bottom: 0,
                }}
            >
                <LogoContainer>{collapsed ? 'A' : 'Admin Panel'}</LogoContainer>
                <Menu
                    theme="dark"
                    mode="inline" // Vertical menu
                    selectedKeys={getSelectedKeys()} // Highlight based on current route
                    items={menuItems} // Menu items defined above
                />
            </Sider>

            {/* Main Content Area Layout */}
             {/* Adjust left margin based on sidebar state */}
            <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
                {/* Header */}
                <StyledHeader>
                    {/* Sidebar Collapse Trigger Button */}
                    <Button
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                        style={{
                            fontSize: '16px',
                            width: 64,
                            height: 64,
                            marginLeft: -24 // Align with header padding start
                        }}
                    />
                    {/* Header Right Side Items */}
                    <HeaderRight>
                        {/* Notification Popover Trigger */}
                        <Popover
                            content={notificationPopoverContent}
                            title={<div style={{ padding: '8px 12px', fontWeight: 500, borderBottom: '1px solid #f0f0f0' }}>Notifications</div>}
                            trigger="click" // Open on click
                            placement="bottomRight" // Position relative to icon
                            open={notificationPopoverVisible} // Control visibility with state
                            onOpenChange={setNotificationPopoverVisible} // Handler to update state
                            overlayStyle={{ paddingTop: '10px' }} // Add space below header
                            arrow={false} // Optional: remove arrow
                        >
                             {/* Badge showing unread count */}
                            <Badge count={unreadCount} size="small" offset={[-4, 4]} title={`${unreadCount} unread notifications`}>
                                <Button type="text" shape="circle" icon={<BellOutlined style={{ fontSize: '20px' }} />} />
                            </Badge>
                        </Popover>

                        {/* User Menu Popover Trigger */}
                        <Popover
                             content={userMenuContent} // Content defined above
                             trigger="click"
                             placement="bottomRight"
                             open={userPopoverVisible} // Control visibility with state
                             onOpenChange={setUserPopoverVisible} // Handler to update state
                             arrow={false} // Optional: remove arrow
                        >
                             {/* Clickable area for user info/avatar */}
                            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '0 8px' }}>
                                <Avatar style={{ backgroundColor: '#87d068', marginRight: '10px' }} icon={<UserOutlined />} size="default" />
                                <UserInfo>
                                    {/* Display user name and role */}
                                    <Text strong style={{ color: '#333', lineHeight: 1.1 }}>{user?.name || 'User'}</Text>
                                    <Text type="secondary" style={{ textTransform: 'capitalize', fontSize: '0.85em', lineHeight: 1.1 }}>{user?.role || 'Role'}</Text>
                                </UserInfo>
                            </div>
                        </Popover>
                    </HeaderRight>
                </StyledHeader>

                {/* Main Content Area */}
                <Content
                    style={{
                        margin: '24px 16px', // Standard Ant Design margins
                        padding: 24, // Standard Ant Design padding
                        minHeight: 'calc(100vh - 64px - 70px)', // Adjust min-height calculation based on header/footer height
                        background: '#fff', // White background for content
                        borderRadius: '8px', // Rounded corners
                        // Optional: Add overflow if content might exceed viewport height
                        // overflow: 'initial',
                    }}
                >
                    {/* Nested routes defined in App.js will render here */}
                    <Outlet />
                </Content>

                {/* Footer */}
                <Footer style={{ textAlign: 'center', background: 'transparent', padding: '15px 50px', color: '#aaa', fontSize: '0.9em' }}>
                    Admin Dashboard ©{new Date().getFullYear()} Created by YourName/Company
                </Footer>
            </Layout>
        </StyledLayout>
    );
};

export default AdminLayout;